import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import apiAxios from 'helpers/apiAxios';
import { handleAsyncThunkError } from 'redux/reduxHelpers';

const BASE = '/crm/dashboard/waivers';

const makeThunk = (key, path, message) => createAsyncThunk(
  `dashboardWaiver/${key}`,
  async (params, { rejectWithValue }) => {
    try {
      const res = await apiAxios.get(`${BASE}/${path}`, { params });
      return res.data.data;
    } catch (e) {
      return rejectWithValue(handleAsyncThunkError(e, message));
    }
  }
);

export const fetchWaiverSummary       = makeThunk('fetchSummary',       'get_summary_data',       'Failed to fetch waiver summary');
export const fetchWaiverTrend         = makeThunk('fetchTrend',         'get_trend_data',         'Failed to fetch waiver trend');
export const fetchWaiverStatus        = makeThunk('fetchStatus',        'get_status_data',        'Failed to fetch waiver status breakdown');
export const fetchWaiverOverdue       = makeThunk('fetchOverdue',       'get_overdue_data',       'Failed to fetch overdue waivers');
export const fetchWaiverCseIncharge   = makeThunk('fetchCseIncharge',   'get_cse_incharge_data',  'Failed to fetch CSE incharge stats');
export const fetchWaiverSalesUnit     = makeThunk('fetchSalesUnit',     'get_sales_unit_data',    'Failed to fetch sales unit stats');
export const fetchWaiverRegion        = makeThunk('fetchRegion',        'get_region_data',        'Failed to fetch region stats');
export const fetchWaiverSalesIncharge = makeThunk('fetchSalesIncharge', 'get_sales_incharge_data','Failed to fetch sales incharge stats');
export const fetchWaiverFilterOptions = makeThunk('fetchFilterOptions', 'filter_options',         'Failed to fetch filter options');

export const fetchWaiverDetailed = createAsyncThunk(
  'dashboardWaiver/fetchDetailed',
  async (params, { rejectWithValue }) => {
    try {
      const res = await apiAxios.get(`${BASE}/get_detailed_data`, { params });
      return res.data;
    } catch (e) {
      return rejectWithValue(handleAsyncThunkError(e, 'Failed to fetch waiver records'));
    }
  }
);

const makeSection = (arrayDefault = false) => ({
  data:    arrayDefault ? [] : null,
  loading: false,
  error:   null
});

const initialState = {
  summary:       makeSection(),
  trend:         makeSection(true),
  status:        makeSection(true),
  overdue:       makeSection(),
  cseIncharge:   makeSection(true),
  salesUnit:     makeSection(true),
  region:        makeSection(true),
  salesIncharge: makeSection(true),
  filterOptions: makeSection(),
  detailed: {
    rows:       [],
    totalRows:  0,
    page:       1,
    perPage:    50,
    loading:    false,
    error:      null
  },
  lastUpdated: null
};

const thunkMap = [
  ['summary',       fetchWaiverSummary],
  ['trend',         fetchWaiverTrend],
  ['status',        fetchWaiverStatus],
  ['overdue',       fetchWaiverOverdue],
  ['cseIncharge',   fetchWaiverCseIncharge],
  ['salesUnit',     fetchWaiverSalesUnit],
  ['region',        fetchWaiverRegion],
  ['salesIncharge', fetchWaiverSalesIncharge],
  ['filterOptions', fetchWaiverFilterOptions],
];

const dashboardWaiverSlice = createSlice({
  name: 'dashboardWaiver',
  initialState,
  reducers: {
    resetWaiverDashboard: () => initialState,
  },
  extraReducers: (builder) => {
    thunkMap.forEach(([key, thunk]) => {
      builder
        .addCase(thunk.pending, (state) => {
          state[key].loading = true;
          state[key].error   = null;
        })
        .addCase(thunk.fulfilled, (state, action) => {
          state[key].data    = action.payload;
          state[key].loading = false;
          state.lastUpdated  = new Date().toISOString();
        })
        .addCase(thunk.rejected, (state, action) => {
          state[key].loading = false;
          state[key].error   = action.payload;
        });
    });

    builder
      .addCase(fetchWaiverDetailed.pending, (state) => {
        state.detailed.loading = true;
        state.detailed.error   = null;
      })
      .addCase(fetchWaiverDetailed.fulfilled, (state, action) => {
        state.detailed.rows      = action.payload.rows || [];
        state.detailed.totalRows = action.payload.total_rows || 0;
        state.detailed.page      = action.payload.page || 1;
        state.detailed.perPage   = action.payload.per_page || 50;
        state.detailed.loading   = false;
        state.lastUpdated        = new Date().toISOString();
      })
      .addCase(fetchWaiverDetailed.rejected, (state, action) => {
        state.detailed.loading = false;
        state.detailed.error   = action.payload;
      });
  }
});

export const { resetWaiverDashboard } = dashboardWaiverSlice.actions;
export default dashboardWaiverSlice.reducer;
